import { useEffect, useMemo, useState } from "react";
import { Loader2, Sparkles, Eye, Trash2, RefreshCw, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  fetchArticles, fetchBriefQueue, generateDraft, deleteArticle, updateArticle,
  type BriefQueueRow,
} from "@/lib/contentOps/api";
import type { ArticleRow } from "@/lib/contentOps/types";
import type { BriefStatus } from "@/lib/contentOps/types";
import { STATUS_META, type ContentStatus } from "@/lib/contentOps/statuses";


interface Props { onOpenArticle: (id: string) => void }

interface QueueItem {
  article: ArticleRow;
  brief?: BriefQueueRow["brief"];
}

const BRIEF_STATUS_META: Record<BriefStatus, { label: string; color: string; bg: string }> = {
  draft:     { label: "Draft",     color: "hsl(0 0% 75%)",    bg: "hsl(0 0% 14%)" },
  in_review: { label: "In Review", color: "hsl(38 92% 70%)",  bg: "hsl(38 92% 50% / 0.14)" },
  approved:  { label: "Approved",  color: "hsl(142 70% 70%)", bg: "hsl(142 70% 35% / 0.14)" },
  rejected:  { label: "Rejected",  color: "hsl(0 75% 70%)",   bg: "hsl(0 75% 45% / 0.14)" },
};

const intentLabel = (i?: string | null) => i ? i[0].toUpperCase() + i.slice(1) : null;

const DraftQueueView = ({ onOpenArticle }: Props) => {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | BriefStatus>("all");
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [arts, briefs] = await Promise.all([fetchArticles(), fetchBriefQueue()]);
      const briefByArt = new Map(briefs.map((r) => [r.article.id, r.brief]));
      // Surface briefs (any status) + any in-flight draft article
      const list: QueueItem[] = [];
      for (const r of briefs) {
        const a = arts.find((x) => x.id === r.article.id);
        if (a) list.push({ article: a, brief: r.brief });
      }
      for (const a of arts) {
        if (briefByArt.has(a.id)) continue;
        if (["draft", "human_review", "seo_review", "ready", "refresh_needed"].includes(a.status)) {
          list.push({ article: a });
        }
      }
      // Sort: in_review > approved > draft > rejected, then by updated
      const order: Record<string, number> = { in_review: 0, approved: 1, draft: 2, rejected: 3 };
      list.sort((x, y) => {
        const xo = order[x.brief?.status ?? "draft"] ?? 4;
        const yo = order[y.brief?.status ?? "draft"] ?? 4;
        if (xo !== yo) return xo - yo;
        return new Date(y.article.updated_at).getTime() - new Date(x.article.updated_at).getTime();
      });
      setItems(list);
    } catch (e) {
      toast.error("Failed to load queue", { description: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() =>
    filter === "all" ? items : items.filter((it) => (it.brief?.status ?? "draft") === filter),
    [items, filter]);

  const genDraft = async (id: string) => {
    setBusyId(id);
    try {
      const hasContent = (items.find((i) => i.article.id === id)?.article.content?.length ?? 0) > 0;
      await generateDraft(id, hasContent ? "regenerate" : "draft");
      toast.success(hasContent ? "Draft regenerated" : "Draft generated");
      load();
    } catch (e) {
      toast.error("Generation failed", { description: (e as Error).message });
    } finally { setBusyId(null); }
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this brief and its article? This cannot be undone.")) return;
    setBusyId(id);
    try {
      await deleteArticle(id);
      setItems((prev) => prev.filter((i) => i.article.id !== id));
      toast.success("Removed");
    } catch (e) {
      toast.error("Delete failed", { description: (e as Error).message });
    } finally { setBusyId(null); }
  };

  const advance = async (article: ArticleRow, next: ContentStatus, label: string) => {
    setBusyId(article.id);
    try {
      await updateArticle(article.id, { status: next });
      toast.success(label);
      load();
    } catch (e) {
      toast.error("Stage change failed", { description: (e as Error).message });
    } finally { setBusyId(null); }
  };

  const FILTERS: { key: "all" | BriefStatus; label: string }[] = [
    { key: "all",       label: "All" },
    { key: "in_review", label: "In Review" },
    { key: "approved",  label: "Approved" },
    { key: "draft",     label: "Draft" },
    { key: "rejected",  label: "Rejected" },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => {
          const count = f.key === "all"
            ? items.length
            : items.filter((it) => (it.brief?.status ?? "draft") === f.key).length;
          const active = filter === f.key;
          return (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors"
              style={{
                background: active ? "hsl(207 68% 50% / 0.18)" : "hsl(0 0% 7%)",
                color: active ? "hsl(207 68% 80%)" : "hsl(0 0% 75%)",
                border: `1px solid ${active ? "hsl(207 68% 50% / 0.5)" : "hsl(0 0% 16%)"}`,
              }}>
              {f.label}
              <span className="tabular-nums" style={{ color: active ? "hsl(207 68% 70%)" : "hsl(0 0% 50%)" }}>{count}</span>
            </button>
          );
        })}
        <Button variant="ghost" size="sm" onClick={load}
          className="ml-auto text-xs" style={{ color: "hsl(0 0% 65%)" }}>
          <RefreshCw size={12} className="mr-1" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40" style={{ color: "hsl(0 0% 50%)" }}>
          <Loader2 size={16} className="animate-spin mr-2" /> Loading queue…
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border p-12 text-center"
          style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
          <p className="text-base font-medium" style={{ color: "hsl(0 0% 80%)" }}>No briefs in this view</p>
          <p className="text-sm mt-1" style={{ color: "hsl(0 0% 55%)" }}>
            Approve a brief in the Brief Generator to put it into production.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(({ article, brief }) => {
            const bs: BriefStatus = (brief?.status as BriefStatus) ?? "draft";
            const sm = BRIEF_STATUS_META[bs];
            const intent = intentLabel(brief?.search_intent);
            const sections = (article.content?.length ?? brief?.outline.length ?? 0);
            const faqs = article.faqs?.length ?? 0;
            const links = article.related_slugs?.length ?? 0;
            const kw = brief?.primary_keyword || article.target_keywords?.[0] || article.title;
            const busy = busyId === article.id;
            const hasContent = (article.content?.length ?? 0) > 0;
            const astatus = (article.status as ContentStatus);
            const astatusMeta = STATUS_META[astatus];

            // Sinai-style next-stage action: progress article through editorial pipeline
            let stageAction: { label: string; next: ContentStatus; toast: string } | null = null;
            if (bs === "approved" && !hasContent) stageAction = null; // primary CTA handled by Generate
            else if (hasContent && astatus === "draft") stageAction = { label: "Send to Human Review", next: "human_review", toast: "Sent to Human Review" };
            else if (astatus === "human_review") stageAction = { label: "Send to SEO Review", next: "seo_review", toast: "Sent to SEO Review" };
            else if (astatus === "seo_review") stageAction = { label: "Mark Ready", next: "ready", toast: "Marked Ready, open Publish queue" };

            return (
              <div key={article.id} className="rounded-xl border p-4 flex flex-wrap gap-4 items-center"
                style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="text-[15px] font-semibold truncate" style={{ color: "hsl(0 0% 95%)" }}>
                      {article.title || "Untitled"}
                    </h4>
                    <Chip color={sm.color} bg={sm.bg}>Brief · {sm.label}</Chip>
                    <Chip color={astatusMeta.color} bg={astatusMeta.bg}>Stage · {astatusMeta.label}</Chip>
                    <Chip color="hsl(42 55% 75%)" bg="hsl(42 55% 35% / 0.14)">{article.category}</Chip>
                    {intent && <Chip color="hsl(207 68% 75%)" bg="hsl(207 68% 40% / 0.14)">{intent}</Chip>}
                  </div>
                  <div className="text-xs" style={{ color: "hsl(0 0% 65%)" }}>
                    <span style={{ color: "hsl(0 0% 50%)" }}>KW:</span> {kw}
                    <span className="mx-1.5" style={{ color: "hsl(0 0% 30%)" }}>·</span>
                    {sections} sections
                    <span className="mx-1.5" style={{ color: "hsl(0 0% 30%)" }}>·</span>
                    {faqs} FAQs
                    <span className="mx-1.5" style={{ color: "hsl(0 0% 30%)" }}>·</span>
                    {links} links
                  </div>
                  <div className="text-[11px]" style={{ color: "hsl(0 0% 45%)" }}>
                    Created {new Date(article.created_at).toLocaleString()}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                  <Button size="sm" disabled={busy || bs !== "approved"}
                    title={bs !== "approved" ? "Approve the brief first to unlock drafting" : undefined}
                    onClick={() => genDraft(article.id)}
                    className="h-9 font-semibold"
                    style={{ background: "hsl(142 50% 30%)", color: "hsl(42 60% 90%)" }}>
                    {busy ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Sparkles size={13} className="mr-1.5" />}
                    {hasContent ? "Regenerate" : "Generate draft"}
                  </Button>
                  {stageAction && (
                    <Button size="sm" disabled={busy}
                      onClick={() => advance(article, stageAction!.next, stageAction!.toast)}
                      className="h-9 font-semibold"
                      style={{ background: "hsl(207 68% 45%)", color: "white" }}>
                      {stageAction.label} <ArrowRight size={13} className="ml-1.5" />
                    </Button>
                  )}
                  {astatus === "ready" && (
                    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold"
                      style={{ background: "hsl(142 70% 35% / 0.16)", color: "hsl(142 70% 75%)" }}>
                      <Check size={11} /> Ready to publish
                    </span>
                  )}
                  <Button size="sm" variant="outline" onClick={() => onOpenArticle(article.id)}
                    className="h-9 border" style={{ borderColor: "hsl(0 0% 22%)", color: "hsl(0 0% 90%)", background: "transparent" }}>
                    <Eye size={13} className="mr-1.5" /> Open
                  </Button>
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => remove(article.id)}
                    className="h-9 w-9 p-0" style={{ color: "hsl(0 0% 55%)" }}><Trash2 size={13} /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


const Chip = ({ color, bg, children }: { color: string; bg: string; children: React.ReactNode }) => (
  <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
    style={{ background: bg, color }}>{children}</span>
);

export default DraftQueueView;
